import AgentVisualizerSection from './AgentVisualizerSection';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ArrowLeftIcon,
  ShieldCheckIcon,
  BellAlertIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PhoneIcon,
  SpeakerWaveIcon,
  PlayIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import { VoiceSelector, PhoneNumberMultiSelect } from './VoiceChannelComponents';
import SearchableSelect from './shared/SearchableSelect';
import FinanceCapabilities from './AgentCapabilities/FinanceCapabilities';
import HRCapabilities from './AgentCapabilities/HRCapabilities';
import ITCapabilities from './AgentCapabilities/ITCapabilities';
import SalesCapabilities from './AgentCapabilities/SalesCapabilities';
import LegalCapabilities from './AgentCapabilities/LegalCapabilities';
import SupportCapabilities from './AgentCapabilities/SupportCapabilities';
import MarketingCapabilities from './AgentCapabilities/MarketingCapabilities';
import logger from '../../../utils/logger';

const AIAgentConfig = ({ showNotification }) => {
  const navigate = useNavigate();
  const { agentId } = useParams();

  // State
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    voiceChannel: true,
    meetingVoice: false,
    guardrails: true,
    escalation: true,
    workingHours: true,
    erpActions: false,
    departmentCapabilities: false
  });

  // Section-specific state
  const [guardrails, setGuardrails] = useState({
    autoApprove: [],
    requireApproval: [],
    spendingLimit: 100,
    dataAccessControls: {
      canAccessAllDepartments: false,
      canAccessPII: false,
      canAccessFinancialData: false,
      canAccessConfidentialData: false,
      auditAllActions: true
    }
  });

  const [escalation, setEscalation] = useState({
    reportToUserId: '',
    sentimentThreshold: -0.5,
    complexityThreshold: 75,
    minConfidence: 70,
    urgencyKeywords: [],
    escalateOn: [],
    escalateAfter: {
      attempts: 3,
      minutes: 60
    }
  });

  const [workingHours, setWorkingHours] = useState({
    enabled: false,
    timezone: 'America/New_York',
    responseTargets: {
      urgent: 1,
      standard: 5,
      low: 15
    },
    afterHoursBehavior: 'full_service',
    outOfOfficeMessage: '',
    schedule: [
      { day: 'monday', startTime: '09:00', endTime: '17:00' },
      { day: 'tuesday', startTime: '09:00', endTime: '17:00' },
      { day: 'wednesday', startTime: '09:00', endTime: '17:00' },
      { day: 'thursday', startTime: '09:00', endTime: '17:00' },
      { day: 'friday', startTime: '09:00', endTime: '17:00' }
    ]
  });

  // Basic info editing
  const [description, setDescription] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [personality, setPersonality] = useState({ tone: 'professional', responseStyle: 'concise' });
  const [originalPersonality, setOriginalPersonality] = useState({ tone: 'professional', responseStyle: 'concise' });

  // Additional profile fields
  const [profilePhoto, setProfilePhoto] = useState('');
  const [originalProfilePhoto, setOriginalProfilePhoto] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [originalJobTitle, setOriginalJobTitle] = useState('');
  // Structured role from the RoleCatalog (department-scoped); selecting one
  // also fills the Job Title field. Stored key only — title/level resolve
  // server-side against the catalog.
  const [roleKey, setRoleKey] = useState('');
  const [originalRoleKey, setOriginalRoleKey] = useState('');
  const [roleCatalog, setRoleCatalog] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [originalPhoneNumber, setOriginalPhoneNumber] = useState('');
  const [signature, setSignature] = useState('');
  const [originalSignature, setOriginalSignature] = useState('');
  const DEFAULT_VISUALIZER = { enabled: true, style: 'aura', color: '#f97316', colorShift: 0.05 };
  const [visualizerConfig, setVisualizerConfig] = useState(DEFAULT_VISUALIZER);
  const [originalVisualizerConfig, setOriginalVisualizerConfig] = useState(DEFAULT_VISUALIZER);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  const signatureRef = useRef(null);

  const [voiceChannel, setVoiceChannel] = useState({
    enabled: false,
    phoneNumbers: [],
    voiceId: 'rachel',
    voiceSettings: {
      stability: 0.75,
      similarity: 0.85
    },
    callBehavior: {
      maxDuration: 15,
      onMaxDuration: 'transfer_to_human',
      voicemailBehavior: 'leave_message',
      recordCalls: true,
      transcribeCalls: true
    }
  });

  // Meeting voice (Cartesia)
  const [meetingVoice, setMeetingVoice] = useState({
    voiceId: '',
    gender: '',
    speakingRate: 1.0
  });
  const [cartesiaVoices, setCartesiaVoices] = useState([]);
  const [loadingCartesiaVoices, setLoadingCartesiaVoices] = useState(false);
  const [syncingVoices, setSyncingVoices] = useState(false);
  const [previewingVoiceId, setPreviewingVoiceId] = useState(null);
  const previewAudioRef = useRef(null);
  const previewBlobUrlRef = useRef(null);

  // DID phone numbers (Telnyx)
  const [didNumbers, setDidNumbers] = useState([]);
  const [loadingDIDs, setLoadingDIDs] = useState(false);

  // Save states for each section
  const [savingGuardrails, setSavingGuardrails] = useState(false);
  const [savingEscalation, setSavingEscalation] = useState(false);
  const [savingWorkingHours, setSavingWorkingHours] = useState(false);
  const [savingVoiceChannel, setSavingVoiceChannel] = useState(false);
  const [savingBasicInfo, setSavingBasicInfo] = useState(false);
  const [savingMeetingVoice, setSavingMeetingVoice] = useState(false);

  // Guardrail testing
  const [guardrailTestAction, setGuardrailTestAction] = useState('');
  const [guardrailTestAmount, setGuardrailTestAmount] = useState('');
  const [guardrailTestResult, setGuardrailTestResult] = useState(null);
  const [testingGuardrail, setTestingGuardrail] = useState(false);

  // Escalation testing
  const [escalationTestParams, setEscalationTestParams] = useState({
    sentiment: 0, complexity: 50, confidence: 80, content: ''
  });
  const [escalationTestResult, setEscalationTestResult] = useState(null);
  const [testingEscalation, setTestingEscalation] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');

  // ERP instances
  const [erpInstances, setErpInstances] = useState([]);
  const [selectedErpInstanceId, setSelectedErpInstanceId] = useState(null);
  const [originalErpInstanceId, setOriginalErpInstanceId] = useState(null);
  const [loadingErpData, setLoadingErpData] = useState(false);
  const [savingErpInstance, setSavingErpInstance] = useState(false);

  // Original states for change detection
  const [originalGuardrails, setOriginalGuardrails] = useState(null);
  const [originalEscalation, setOriginalEscalation] = useState(null);
  const [originalWorkingHours, setOriginalWorkingHours] = useState(null);
  const [originalVoiceChannel, setOriginalVoiceChannel] = useState(null);
  const [originalMeetingVoice, setOriginalMeetingVoice] = useState(null);

  // Basic info editing
  const [agentName, setAgentName] = useState('');
  const [originalAgentName, setOriginalAgentName] = useState('');

  // Fetch agent config
  useEffect(() => {
    fetchAgentConfig();
    fetchUsers();
  }, [agentId]);

  // Cleanup preview audio and blob URLs on unmount
  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
      if (previewBlobUrlRef.current) {
        URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = null;
      }
    };
  }, []);

  const fetchAgentConfig = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/ai/agents/${agentId}/config`);

      if (response.data.success) {
        const agentData = response.data.data;
        setAgent(agentData);

        // Set agent name
        setAgentName(agentData.agentName || '');
        setOriginalAgentName(agentData.agentName || '');

        // Set description
        setDescription(agentData.description || '');
        setOriginalDescription(agentData.description || '');

        // Set profile fields
        const vz = { enabled: true, style: 'aura', color: '#f97316', colorShift: 0.05, ...(agentData.visualizerConfig || {}) };
        setVisualizerConfig(vz);
        setOriginalVisualizerConfig(vz);
        setProfilePhoto(agentData.profilePhoto || '');
        setOriginalProfilePhoto(agentData.profilePhoto || '');
        setJobTitle(agentData.jobTitle || '');
        setOriginalJobTitle(agentData.jobTitle || '');
        setRoleKey(agentData.role?.key || '');
        setOriginalRoleKey(agentData.role?.key || '');
        try {
          const rc = await axiosInstance.get('/api/ai/role-catalog', { params: { department: agentData.department } });
          setRoleCatalog(rc.data.data || []);
        } catch (_) { /* non-fatal: Role select shows no options */ }
        setPhoneNumber(agentData.phoneNumber || '');
        setOriginalPhoneNumber(agentData.phoneNumber || '');
        setSignature(agentData.emailConfig?.signature || '');
        setOriginalSignature(agentData.emailConfig?.signature || '');

        // Set personality
        const pers = {
          tone: agentData.personality?.tone || 'professional',
          responseStyle: agentData.personality?.responseStyle || 'concise'
        };
        setPersonality(pers);
        setOriginalPersonality(JSON.parse(JSON.stringify(pers)));

        // Set guardrails
        const gr = {
          autoApprove: agentData.guardrails?.autoApprove || [],
          requireApproval: agentData.guardrails?.requireApproval || [],
          spendingLimit: agentData.guardrails?.spendingLimit || 100,
          dataAccessControls: {
            canAccessAllDepartments: agentData.guardrails?.dataAccessControls?.canAccessAllDepartments || false,
            canAccessPII: agentData.guardrails?.dataAccessControls?.canAccessPII || false,
            canAccessFinancialData: agentData.guardrails?.dataAccessControls?.canAccessFinancialData || false,
            canAccessConfidentialData: agentData.guardrails?.dataAccessControls?.canAccessConfidentialData || false,
            auditAllActions: agentData.guardrails?.dataAccessControls?.auditAllActions ?? true
          }
        };
        setGuardrails(gr);
        setOriginalGuardrails(JSON.parse(JSON.stringify(gr)));

        // Set escalation
        const esc = {
          reportToUserId: agentData.escalation?.reportToUserId || '',
          sentimentThreshold: agentData.escalation?.sentimentThreshold ?? -0.5,
          complexityThreshold: agentData.escalation?.complexityThreshold ?? 75,
          minConfidence: agentData.escalation?.minConfidence ?? 70,
          urgencyKeywords: agentData.escalation?.urgencyKeywords || [],
          escalateOn: agentData.escalation?.escalateOn || [],
          escalateAfter: {
            attempts: agentData.escalation?.escalateAfter?.attempts ?? 3,
            minutes: agentData.escalation?.escalateAfter?.minutes ?? 60
          }
        };
        setEscalation(esc);
        setOriginalEscalation(JSON.parse(JSON.stringify(esc)));

        // Set working hours
        const defaultSchedule = [
          { day: 'monday', startTime: '09:00', endTime: '17:00' },
          { day: 'tuesday', startTime: '09:00', endTime: '17:00' },
          { day: 'wednesday', startTime: '09:00', endTime: '17:00' },
          { day: 'thursday', startTime: '09:00', endTime: '17:00' },
          { day: 'friday', startTime: '09:00', endTime: '17:00' }
        ];
        const wh = {
          enabled: agentData.workingHours?.enabled || false,
          timezone: agentData.workingHours?.timezone || 'America/New_York',
          responseTargets: {
            urgent: agentData.workingHours?.responseTargets?.urgent ?? 1,
            standard: agentData.workingHours?.responseTargets?.standard ?? 5,
            low: agentData.workingHours?.responseTargets?.low ?? 15
          },
          afterHoursBehavior: agentData.workingHours?.afterHoursBehavior || 'full_service',
          outOfOfficeMessage: agentData.workingHours?.outOfOfficeMessage || '',
          schedule: agentData.workingHours?.schedule?.length > 0
            ? agentData.workingHours.schedule
            : defaultSchedule
        };
        setWorkingHours(wh);
        setOriginalWorkingHours(JSON.parse(JSON.stringify(wh)));

        // Set voice channel

        const vc = {
          enabled: agentData.voiceConfig?.enabled || false,
          phoneNumbers: agentData.voiceConfig?.phoneNumbers?.map(p => p._id || p) || [],
          voiceId: agentData.voiceConfig?.voiceId || 'rachel',
          voiceSettings: {
            stability: agentData.voiceConfig?.voiceSettings?.stability ?? 0.75,
            similarity: agentData.voiceConfig?.voiceSettings?.similarity ?? 0.85
          },
          callBehavior: {
            maxDuration: agentData.voiceConfig?.callBehavior?.maxDuration ?? 15,
            onMaxDuration: agentData.voiceConfig?.callBehavior?.onMaxDuration || 'transfer_to_human',
            voicemailBehavior: agentData.voiceConfig?.callBehavior?.voicemailBehavior || 'leave_message',
            recordCalls: agentData.voiceConfig?.callBehavior?.recordCalls ?? true,
            transcribeCalls: agentData.voiceConfig?.callBehavior?.transcribeCalls ?? true
          }
        };

        setVoiceChannel(vc);
        setOriginalVoiceChannel(JSON.parse(JSON.stringify(vc)));

        // Set meeting voice (Cartesia)
        const mv = {
          voiceId: agentData.meetingConfig?.meetingVoice?.voiceId || '',
          gender: agentData.meetingConfig?.meetingVoice?.gender || '',
          speakingRate: agentData.meetingConfig?.meetingVoice?.speakingRate ?? 1.0
        };
        setMeetingVoice(mv);
        setOriginalMeetingVoice(JSON.parse(JSON.stringify(mv)));

        // Set ERP instance
        const erpId = agentData.erpInstanceId?._id || agentData.erpInstanceId || null;
        setSelectedErpInstanceId(erpId);
        setOriginalErpInstanceId(erpId);

        // Fetch DID phone numbers
        fetchDIDs();
      }
    } catch (error) {
      logger.error('Error fetching agent config:', error);
      showNotification?.('Failed to load agent configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axiosInstance.get('/api/business/users', { params: { limit: 1000 } });
      const userData = response.data.users || response.data.data || [];
      setUsers(userData);
    } catch (error) {
      logger.error('Error fetching users:', error);
      showNotification?.('Failed to load users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchDIDs = async () => {
    try {
      setLoadingDIDs(true);
      const response = await axiosInstance.get('/api/dids');
      if (response.data.success) {
        const numbers = response.data.dids || [];

        logger.log('[AIAgentConfig] DIDs assigned to agent:', numbers.filter(n =>
          n.assignment?.type === 'agent' &&
          (n.assignment?.agentId?._id || n.assignment?.agentId)?.toString() === agentId?.toString()
        ));
        setDidNumbers(numbers);
      }
    } catch (error) {
      logger.error('Error fetching DIDs:', error);
      showNotification?.('Failed to load phone numbers', 'error');
    } finally {
      setLoadingDIDs(false);
    }
  };

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Check if section has changes
  const hasBasicInfoChanges = () => {
    return agentName !== originalAgentName
      || description !== originalDescription
      || jobTitle !== originalJobTitle
      || roleKey !== originalRoleKey
      || phoneNumber !== originalPhoneNumber
      || signature !== originalSignature
      || JSON.stringify(personality) !== JSON.stringify(originalPersonality)
      || JSON.stringify(visualizerConfig) !== JSON.stringify(originalVisualizerConfig);
  };

  const hasGuardrailsChanges = () => {
    return JSON.stringify(guardrails) !== JSON.stringify(originalGuardrails);
  };

  const hasEscalationChanges = () => {
    return JSON.stringify(escalation) !== JSON.stringify(originalEscalation);
  };

  const hasWorkingHoursChanges = () => {
    return JSON.stringify(workingHours) !== JSON.stringify(originalWorkingHours);
  };

  const hasVoiceChannelChanges = () => {
    return JSON.stringify(voiceChannel) !== JSON.stringify(originalVoiceChannel);
  };

  const hasMeetingVoiceChanges = () => {
    return JSON.stringify(meetingVoice) !== JSON.stringify(originalMeetingVoice);
  };

  // Save handlers
  const saveBasicInfo = async () => {
    try {
      setSavingBasicInfo(true);
      const response = await axiosInstance.put(`/api/ai/agents/${agentId}/basic-info`, {
        agentName: agentName.trim(),
        description: description.trim(),
        jobTitle: jobTitle.trim(),
        // '' clears the structured role server-side; only send when changed
        // so a plain jobTitle edit never touches the role.
        ...(roleKey !== originalRoleKey ? { roleKey: roleKey || null } : {}),
        phoneNumber: phoneNumber.trim(),
        signature,
        personality,
        visualizerConfig
      });

      if (response.data.success) {
        setOriginalAgentName(agentName);
        setOriginalDescription(description);
        setOriginalJobTitle(jobTitle);
        setOriginalRoleKey(roleKey);
        setOriginalPhoneNumber(phoneNumber);
        setOriginalSignature(signature);
        setOriginalPersonality(JSON.parse(JSON.stringify(personality)));
        setOriginalVisualizerConfig(JSON.parse(JSON.stringify(visualizerConfig)));
        setAgent(prev => ({
          ...prev,
          agentName,
          description,
          jobTitle,
          phoneNumber,
          personality,
          emailConfig: { ...(prev?.emailConfig || {}), signature }
        }));
        showNotification?.('Agent info updated successfully', 'success');
      }
    } catch (error) {
      logger.error('Error saving basic info:', error);
      showNotification?.('Failed to update agent info', 'error');
    } finally {
      setSavingBasicInfo(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showNotification?.('Photo must be under 5MB', 'error');
      return;
    }

    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('photo', file);
      const response = await axiosInstance.post(
        `/api/ai/agents/${agentId}/profile-photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        const newPhoto = response.data.data.profilePhoto;
        setProfilePhoto(newPhoto);
        setOriginalProfilePhoto(newPhoto);
        setAgent(prev => ({ ...prev, profilePhoto: newPhoto }));
        showNotification?.('Profile photo updated', 'success');
      }
    } catch (error) {
      logger.error('Error uploading photo:', error);
      showNotification?.(error.response?.data?.message || 'Failed to upload photo', 'error');
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handlePhotoRemove = async () => {
    if (!profilePhoto) return;
    if (!window.confirm('Remove the profile photo?')) return;

    try {
      setUploadingPhoto(true);
      await axiosInstance.delete(`/api/ai/agents/${agentId}/profile-photo`);
      setProfilePhoto('');
      setOriginalProfilePhoto('');
      setAgent(prev => ({ ...prev, profilePhoto: '' }));
      showNotification?.('Profile photo removed', 'success');
    } catch (error) {
      logger.error('Error removing photo:', error);
      showNotification?.('Failed to remove photo', 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Signature editor helpers
  const SIGNATURE_VARIABLES = [
    { token: '{full_name}', label: 'Full Name', description: 'Agent display name' },
    { token: '{job_title}', label: 'Job Title', description: 'Role / position' },
    { token: '{department}', label: 'Department', description: 'Agent department' },
    { token: '{email}', label: 'Email', description: 'Agent email address' },
    { token: '{phone}', label: 'Phone', description: 'Phone number (if assigned)' },
    { token: '{business_name}', label: 'Business', description: 'Organization name' }
  ];

  const insertAtCursor = (text) => {
    const ta = signatureRef.current;
    if (!ta) {
      setSignature(prev => prev + text);
      return;
    }
    const start = ta.selectionStart ?? signature.length;
    const end = ta.selectionEnd ?? signature.length;
    const next = signature.slice(0, start) + text + signature.slice(end);
    if (next.length > 1000) return;
    setSignature(next);
    // Restore cursor after the inserted text
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const applyDividerToSignature = () => {
    insertAtCursor('\n――――――――――――――――\n');
  };

  const applySignatureTemplate = (tpl) => {
    const templates = {
      simple: `{full_name}\n{email}`,
      professional: `{full_name}\n{job_title} | {department}\n{email}${phoneNumber ? '\n{phone}' : ''}\n{business_name}`,
      minimal: `— {full_name}, {department}`,
      contact: `{full_name}\n{job_title}\n📧 {email}\n📞 {phone}\n🏢 {business_name}`
    };
    const next = templates[tpl] || '';
    if (next.length > 1000) return;
    setSignature(next);
    requestAnimationFrame(() => signatureRef.current?.focus());
  };

  const resolveSignaturePreview = (text) => {
    if (!text) return '';
    return text
      .replace(/\{full_name\}/g, agentName || 'Agent Name')
      .replace(/\{job_title\}/g, jobTitle || '')
      .replace(/\{department\}/g, (agent?.department || '').replace(/^\w/, c => c.toUpperCase()))
      .replace(/\{email\}/g, agent?.emailConfig?.address || '')
      .replace(/\{phone\}/g, phoneNumber || '')
      .replace(/\{business_name\}/g, agent?.businessId?.businessName || '');
  };

  const saveGuardrails = async () => {
    try {
      setSavingGuardrails(true);
      const response = await axiosInstance.put(`/api/ai/agents/${agentId}/guardrails`, guardrails);

      if (response.data.success) {
        setOriginalGuardrails(JSON.parse(JSON.stringify(guardrails)));
        showNotification?.('Guardrails updated successfully', 'success');
      }
    } catch (error) {
      logger.error('Error saving guardrails:', error);
      showNotification?.('Failed to save guardrails', 'error');
    } finally {
      setSavingGuardrails(false);
    }
  };

  const saveEscalation = async () => {
    try {
      setSavingEscalation(true);
      const response = await axiosInstance.put(`/api/ai/agents/${agentId}/escalation`, escalation);

      if (response.data.success) {
        setOriginalEscalation(JSON.parse(JSON.stringify(escalation)));
        showNotification?.('Escalation settings updated successfully', 'success');
      }
    } catch (error) {
      logger.error('Error saving escalation:', error);
      showNotification?.('Failed to save escalation settings', 'error');
    } finally {
      setSavingEscalation(false);
    }
  };

  const saveWorkingHours = async () => {
    try {
      setSavingWorkingHours(true);
      const response = await axiosInstance.put(`/api/ai/agents/${agentId}/working-hours`, workingHours);

      if (response.data.success) {
        setOriginalWorkingHours(JSON.parse(JSON.stringify(workingHours)));
        showNotification?.('Working hours updated successfully', 'success');
      }
    } catch (error) {
      logger.error('Error saving working hours:', error);
      showNotification?.('Failed to save working hours', 'error');
    } finally {
      setSavingWorkingHours(false);
    }
  };

  const saveVoiceChannel = async () => {
    try {
      setSavingVoiceChannel(true);
      const response = await axiosInstance.put(`/api/ai/agents/${agentId}/voice/config`, voiceChannel);

      if (response.data.success) {
        setOriginalVoiceChannel(JSON.parse(JSON.stringify(voiceChannel)));
        showNotification?.('Voice channel settings updated successfully', 'success');
      }
    } catch (error) {
      logger.error('Error saving voice channel:', error);
      showNotification?.('Failed to save voice channel settings', 'error');
    } finally {
      setSavingVoiceChannel(false);
    }
  };

  const saveMeetingVoice = async () => {
    try {
      setSavingMeetingVoice(true);
      const response = await axiosInstance.put(`/api/ai/agents/${agentId}/meeting-voice`, meetingVoice);

      if (response.data.success) {
        setOriginalMeetingVoice(JSON.parse(JSON.stringify(meetingVoice)));
        showNotification?.('Meeting voice updated successfully', 'success');
      }
    } catch (error) {
      logger.error('Error saving meeting voice:', error);
      showNotification?.('Failed to save meeting voice', 'error');
    } finally {
      setSavingMeetingVoice(false);
    }
  };

  // Cancel handlers
  const cancelMeetingVoice = () => {
    setMeetingVoice(JSON.parse(JSON.stringify(originalMeetingVoice)));
  };

  // Fetch Cartesia voices filtered by gender (reads from DB cache)
  const fetchCartesiaVoices = async (gender) => {
    try {
      setLoadingCartesiaVoices(true);
      const url = `/api/ai/voices/cartesia${gender ? `?gender=${gender}` : ''}`;
      const response = await axiosInstance.get(url);
      if (response.data.success) {
        setCartesiaVoices(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error fetching Cartesia voices:', error);
      setCartesiaVoices([]);
    } finally {
      setLoadingCartesiaVoices(false);
    }
  };

  // Sync voices from Cartesia API into DB (manual refresh)
  const syncCartesiaVoices = async () => {
    try {
      setSyncingVoices(true);
      const response = await axiosInstance.post('/api/ai/voices/cartesia/sync');
      if (response.data.success) {
        showNotification?.(`Synced ${response.data.count} voices (${response.data.male} male, ${response.data.female} female)`, 'success');
        // Reload voices with current filter
        fetchCartesiaVoices(meetingVoice.gender || undefined);
      }
    } catch (error) {
      logger.error('Error syncing Cartesia voices:', error);
      showNotification?.('Failed to sync voices from Cartesia', 'error');
    } finally {
      setSyncingVoices(false);
    }
  };

  // Preview a Cartesia voice
  const previewVoice = async (voiceId, voiceName) => {
    try {
      // Stop any currently playing preview
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }

      // If clicking the same voice that's playing, just stop
      if (previewingVoiceId === voiceId) {
        setPreviewingVoiceId(null);
        return;
      }

      setPreviewingVoiceId(voiceId);

      // Check if voice has a preview URL (Cartesia CDN — no API credits used)
      const voice = cartesiaVoices.find(v => v.id === voiceId);
      let audioUrl;
      let isBlobUrl = false;

      if (voice?.previewUrl) {
        audioUrl = voice.previewUrl;
      } else {
        // Fallback: ask backend to check DB or generate TTS
        const response = await axiosInstance.post('/api/ai/voices/cartesia/preview', {
          voiceId,
          text: `Hi there! I'm ${voiceName || 'your AI teammate'}. Looking forward to working with you.`
        }, { responseType: 'blob' });

        // Check if response is JSON (previewUrl redirect) or blob (WAV audio)
        const contentType = response.headers['content-type'];
        if (contentType?.includes('application/json')) {
          const text = await response.data.text();
          const json = JSON.parse(text);
          if (json.previewUrl) {
            audioUrl = json.previewUrl;
          }
        }

        if (!audioUrl) {
          audioUrl = URL.createObjectURL(response.data);
          isBlobUrl = true;
        }
      }

      previewBlobUrlRef.current = isBlobUrl ? audioUrl : null;
      const audio = new Audio(audioUrl);
      previewAudioRef.current = audio;

      audio.onended = () => {
        setPreviewingVoiceId(null);
        if (isBlobUrl) URL.revokeObjectURL(audioUrl);
        previewBlobUrlRef.current = null;
        previewAudioRef.current = null;
      };
      audio.onerror = () => {
        setPreviewingVoiceId(null);
        if (isBlobUrl) URL.revokeObjectURL(audioUrl);
        previewBlobUrlRef.current = null;
        previewAudioRef.current = null;
      };

      await audio.play();
    } catch (error) {
      logger.error('Error previewing voice:', error);
      setPreviewingVoiceId(null);
      showNotification?.('Failed to preview voice', 'error');
    }
  };

  const cancelBasicInfo = () => {
    setAgentName(originalAgentName);
    setDescription(originalDescription);
    setJobTitle(originalJobTitle);
    setPhoneNumber(originalPhoneNumber);
    setSignature(originalSignature);
    setPersonality(JSON.parse(JSON.stringify(originalPersonality)));
  };

  const cancelGuardrails = () => {
    setGuardrails(JSON.parse(JSON.stringify(originalGuardrails)));
  };

  const cancelEscalation = () => {
    setEscalation(JSON.parse(JSON.stringify(originalEscalation)));
  };

  const cancelWorkingHours = () => {
    setWorkingHours(JSON.parse(JSON.stringify(originalWorkingHours)));
  };

  const cancelVoiceChannel = () => {
    setVoiceChannel(JSON.parse(JSON.stringify(originalVoiceChannel)));
  };

  // Test guardrails
  const testGuardrail = async () => {
    if (!guardrailTestAction) return;
    try {
      setTestingGuardrail(true);
      setGuardrailTestResult(null);
      const inputData = guardrailTestAmount ? { amount: parseFloat(guardrailTestAmount) } : {};
      const response = await axiosInstance.post(`/api/ai/agents/${agentId}/check-guardrails`, {
        actionCode: guardrailTestAction,
        inputData
      });
      if (response.data.success) {
        setGuardrailTestResult(response.data.data);
      }
    } catch (error) {
      logger.error('Error testing guardrail:', error);
      setGuardrailTestResult({ approved: false, reason: 'api_error', warnings: [error.message] });
    } finally {
      setTestingGuardrail(false);
    }
  };

  // Test escalation
  const testEscalation = async () => {
    try {
      setTestingEscalation(true);
      setEscalationTestResult(null);
      const response = await axiosInstance.post(`/api/ai/agents/${agentId}/evaluate-escalation`, escalationTestParams);
      if (response.data.success) {
        setEscalationTestResult(response.data.data);
      }
    } catch (error) {
      logger.error('Error testing escalation:', error);
      setEscalationTestResult({ shouldEscalate: false, reasons: [error.message], score: 0 });
    } finally {
      setTestingEscalation(false);
    }
  };

  // Add urgency keyword
  const addUrgencyKeyword = () => {
    const kw = newKeyword.trim();
    if (kw && !escalation.urgencyKeywords.includes(kw)) {
      setEscalation(prev => ({ ...prev, urgencyKeywords: [...prev.urgencyKeywords, kw] }));
      setNewKeyword('');
    }
  };

  // Remove urgency keyword
  const removeUrgencyKeyword = (keyword) => {
    setEscalation(prev => ({
      ...prev,
      urgencyKeywords: prev.urgencyKeywords.filter(k => k !== keyword)
    }));
  };

  // Fetch ERP instances for the business
  const fetchErpInstances = async () => {
    try {
      setLoadingErpData(true);
      const res = await axiosInstance.get('/api/erp/instances');
      if (res.data.success) {
        setErpInstances(res.data.data || []);
      }
    } catch (error) {
      logger.error('Error fetching ERP instances:', error);
    } finally {
      setLoadingErpData(false);
    }
  };

  // Save ERP instance assignment
  const saveErpInstance = async () => {
    try {
      setSavingErpInstance(true);
      const res = await axiosInstance.put(`/api/ai/agents/${agentId}`, {
        erpInstanceId: selectedErpInstanceId || null
      });
      if (res.data.success) {
        setOriginalErpInstanceId(selectedErpInstanceId);
        showNotification?.('ERP instance updated', 'success');
      }
    } catch (error) {
      logger.error('Error saving ERP instance:', error);
      showNotification?.('Failed to update ERP instance', 'error');
    } finally {
      setSavingErpInstance(false);
    }
  };

  // Toggle escalation event
  const toggleEscalateOn = (event) => {
    setEscalation(prev => {
      const current = prev.escalateOn || [];
      const exists = current.includes(event);
      return {
        ...prev,
        escalateOn: exists ? current.filter(e => e !== event) : [...current, event]
      };
    });
  };

  // Helper functions
  const toggleGuardrailAction = (actionType, action) => {
    setGuardrails(prev => {
      const currentActions = prev[actionType] || [];
      const exists = currentActions.includes(action);

      return {
        ...prev,
        [actionType]: exists
          ? currentActions.filter(a => a !== action)
          : [...currentActions, action]
      };
    });
  };

  // User options for searchable select
  const userOptions = users.map(user => {
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email?.split('@')[0] || 'Unknown';
    return { value: user._id || user.id, label: `${name} (${user.email})` };
  });

  // Guardrails options - generic + department-specific
  const genericAutoApprove = [
    { value: 'meetings', label: 'Meetings' },
    { value: 'internal_emails', label: 'Internal Emails' },
    { value: 'voice_calls', label: 'Voice Calls' },
    { value: 'basic_info', label: 'Basic Info' },
    { value: 'create_tasks', label: 'Create Tasks' },
    { value: 'search_knowledge', label: 'Search Knowledge' },
    { value: 'status_updates', label: 'Status Updates' }
  ];

  const deptAutoApproveMap = {
    hr: [
      { value: 'screen_candidate', label: 'Screen Candidate' },
      { value: 'schedule_interview', label: 'Schedule Interview' },
      { value: 'request_documents', label: 'Request Documents' },
      { value: 'send_onboarding_info', label: 'Send Onboarding Info' },
      { value: 'update_candidate_status', label: 'Update Candidate Status' },
      { value: 'send_rejection_email', label: 'Send Rejection Email' }
    ],
    finance: [
      { value: 'log_expense', label: 'Log Expense' },
      { value: 'generate_report', label: 'Generate Report' },
      { value: 'send_payment_reminder', label: 'Send Payment Reminder' },
      { value: 'update_invoice_status', label: 'Update Invoice Status' },
      { value: 'track_budget', label: 'Track Budget' }
    ],
    it: [
      { value: 'create_ticket', label: 'Create Ticket' },
      { value: 'assign_ticket', label: 'Assign Ticket' },
      { value: 'update_ticket_status', label: 'Update Ticket Status' },
      { value: 'provide_tech_support', label: 'Provide Tech Support' },
      { value: 'send_password_reset', label: 'Send Password Reset' },
      { value: 'log_asset', label: 'Log Asset' }
    ],
    sales: [
      { value: 'log_lead', label: 'Log Lead' },
      { value: 'update_lead_status', label: 'Update Lead Status' },
      { value: 'schedule_demo', label: 'Schedule Demo' },
      { value: 'send_follow_up', label: 'Send Follow Up' },
      { value: 'log_activity', label: 'Log Activity' },
      { value: 'update_opportunity', label: 'Update Opportunity' }
    ],
    legal: [
      { value: 'log_contract', label: 'Log Contract' },
      { value: 'send_contract_reminder', label: 'Send Contract Reminder' },
      { value: 'update_contract_status', label: 'Update Contract Status' },
      { value: 'provide_legal_info', label: 'Provide Legal Info' }
    ],
    support: [
      { value: 'create_support_ticket', label: 'Create Support Ticket' },
      { value: 'send_acknowledgment', label: 'Send Acknowledgment' },
      { value: 'provide_solution', label: 'Provide Solution' },
      { value: 'update_ticket', label: 'Update Ticket' },
      { value: 'send_satisfaction_survey', label: 'Send Satisfaction Survey' }
    ],
    marketing: [
      { value: 'schedule_post', label: 'Schedule Post' },
      { value: 'log_campaign_activity', label: 'Log Campaign Activity' },
      { value: 'send_newsletter', label: 'Send Newsletter' },
      { value: 'update_lead_score', label: 'Update Lead Score' },
      { value: 'track_engagement', label: 'Track Engagement' }
    ],
    procurement: [
      { value: 'create_purchase_order', label: 'Create Purchase Order' },
      { value: 'view_vendors', label: 'View Vendors' },
      { value: 'create_grn', label: 'Create Goods Receipt' },
      { value: 'send_rfq', label: 'Send RFQ' },
      { value: 'track_delivery', label: 'Track Delivery' },
      { value: 'view_purchase_orders', label: 'View Purchase Orders' }
    ],
    ops: [
      { value: 'create_work_order', label: 'Create Work Order' },
      { value: 'view_work_orders', label: 'View Work Orders' },
      { value: 'create_production_plan', label: 'Create Production Plan' },
      { value: 'view_quality_inspections', label: 'View Quality Inspections' },
      { value: 'update_work_order_status', label: 'Update Work Order Status' },
      { value: 'log_production_output', label: 'Log Production Output' }
    ],
    management: [
      { value: 'create_lead', label: 'Create Lead' },
      { value: 'view_leads', label: 'View Leads' },
      { value: 'view_opportunities', label: 'View Opportunities' },
      { value: 'view_campaigns', label: 'View Campaigns' },
      { value: 'view_inventory', label: 'View Inventory' },
      { value: 'view_activities', label: 'View Activities' }
    ]
  };

  // Add CRM options to sales department (CRM is typically managed by sales)
  if (deptAutoApproveMap.sales) {
    deptAutoApproveMap.sales = [
      ...deptAutoApproveMap.sales,
      { value: 'create_lead', label: 'Create Lead' },
      { value: 'view_leads', label: 'View Leads' },
      { value: 'assign_lead', label: 'Assign Lead' },
      { value: 'create_contact', label: 'Create Contact' },
      { value: 'view_contacts', label: 'View Contacts' },
      { value: 'create_opportunity', label: 'Create Opportunity' },
      { value: 'view_opportunities', label: 'View Opportunities' },
      { value: 'view_activities', label: 'View Activities' },
      { value: 'score_leads', label: 'Score Leads' },
      { value: 'view_campaigns', label: 'View Campaigns' }
    ];
  }

  const genericRequireApproval = [
    { value: 'external_emails', label: 'External Emails' },
    { value: 'voice_calls', label: 'Voice Calls' },
    { value: 'financial_transactions', label: 'Financial Transactions' },
    { value: 'access_grants', label: 'Access Grants' },
    { value: 'data_changes', label: 'Data Changes' },
    { value: 'contract_actions', label: 'Contract Actions' },
    { value: 'user_management', label: 'User Management' }
  ];

  const deptRequireApprovalMap = {
    hr: [
      { value: 'post_job', label: 'Post Job' },
      { value: 'send_offer_letter', label: 'Send Offer Letter' },
      { value: 'approve_leave', label: 'Approve Leave' },
      { value: 'terminate_employee', label: 'Terminate Employee' },
      { value: 'change_salary', label: 'Change Salary' },
      { value: 'approve_promotion', label: 'Approve Promotion' },
      { value: 'access_employee_records', label: 'Access Employee Records' }
    ],
    finance: [
      { value: 'approve_invoice', label: 'Approve Invoice' },
      { value: 'process_payment', label: 'Process Payment' },
      { value: 'approve_expense', label: 'Approve Expense' },
      { value: 'modify_budget', label: 'Modify Budget' },
      { value: 'approve_refund', label: 'Approve Refund' },
      { value: 'create_financial_report', label: 'Create Financial Report' },
      { value: 'adjust_price', label: 'Adjust Price' }
    ],
    it: [
      { value: 'grant_system_access', label: 'Grant System Access' },
      { value: 'reset_password', label: 'Reset Password' },
      { value: 'provision_device', label: 'Provision Device' },
      { value: 'modify_security_settings', label: 'Modify Security Settings' },
      { value: 'access_sensitive_data', label: 'Access Sensitive Data' },
      { value: 'change_permissions', label: 'Change Permissions' }
    ],
    sales: [
      { value: 'apply_discount', label: 'Apply Discount' },
      { value: 'approve_quote', label: 'Approve Quote' },
      { value: 'modify_pricing', label: 'Modify Pricing' },
      { value: 'close_deal', label: 'Close Deal' },
      { value: 'access_competitor_data', label: 'Access Competitor Data' }
    ],
    legal: [
      { value: 'sign_contract', label: 'Sign Contract' },
      { value: 'approve_legal_document', label: 'Approve Legal Document' },
      { value: 'settle_dispute', label: 'Settle Dispute' },
      { value: 'authorize_legal_action', label: 'Authorize Legal Action' }
    ],
    support: [
      { value: 'issue_refund', label: 'Issue Refund' },
      { value: 'escalate_to_management', label: 'Escalate to Management' },
      { value: 'access_payment_info', label: 'Access Payment Info' },
      { value: 'modify_account', label: 'Modify Account' }
    ],
    marketing: [
      { value: 'publish_campaign', label: 'Publish Campaign' },
      { value: 'approve_content', label: 'Approve Content' },
      { value: 'send_mass_email', label: 'Send Mass Email' },
      { value: 'modify_brand_assets', label: 'Modify Brand Assets' },
      { value: 'access_analytics_data', label: 'Access Analytics Data' }
    ],
    procurement: [
      { value: 'approve_purchase_order', label: 'Approve Purchase Order' },
      { value: 'approve_vendor', label: 'Approve Vendor' },
      { value: 'modify_purchase_terms', label: 'Modify Purchase Terms' },
      { value: 'process_vendor_payment', label: 'Process Vendor Payment' },
      { value: 'approve_grn', label: 'Approve Goods Receipt' },
      { value: 'cancel_purchase_order', label: 'Cancel Purchase Order' }
    ],
    ops: [
      { value: 'approve_work_order', label: 'Approve Work Order' },
      { value: 'modify_production_plan', label: 'Modify Production Plan' },
      { value: 'approve_quality_inspection', label: 'Approve Quality Inspection' },
      { value: 'scrap_materials', label: 'Scrap Materials' },
      { value: 'override_bom', label: 'Override Bill of Materials' }
    ]
  };

  // Add CRM require-approval options to sales department
  if (deptRequireApprovalMap.sales) {
    deptRequireApprovalMap.sales = [
      ...deptRequireApprovalMap.sales,
      { value: 'convert_lead', label: 'Convert Lead to Customer' },
      { value: 'delete_lead', label: 'Delete Lead' },
      { value: 'delete_opportunity', label: 'Delete Opportunity' },
      { value: 'launch_campaign', label: 'Launch Campaign' },
      { value: 'bulk_assign_leads', label: 'Bulk Assign Leads' },
      { value: 'modify_lead_scoring', label: 'Modify Lead Scoring' },
      { value: 'access_crm_analytics', label: 'Access CRM Analytics' },
      { value: 'update_contact', label: 'Update Contact' },
      { value: 'update_opportunity', label: 'Update Opportunity' }
    ];
  }

  const autoApproveOptions = [
    ...genericAutoApprove,
    ...(deptAutoApproveMap[agent?.department] || [])
  ];

  const requireApprovalOptions = [
    ...genericRequireApproval,
    ...(deptRequireApprovalMap[agent?.department] || [])
  ];

  // All testable action codes (deduplicated from current options)
  const allActionCodes = [...new Set([
    ...autoApproveOptions.map(o => o.value),
    ...requireApprovalOptions.map(o => o.value)
  ])];

  // Escalation event options (from IrisAgent model enum)
  const escalationEventOptions = [
    { value: 'high_value_transaction', label: 'High Value Transaction' },
    { value: 'security_risk', label: 'Security Risk' },
    { value: 'policy_violation', label: 'Policy Violation' },
    { value: 'repeated_failure', label: 'Repeated Failure' },
    { value: 'explicit_request', label: 'Explicit Request' },
    { value: 'unknown_issue', label: 'Unknown Issue' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Agent not found</p>
        <button
          onClick={() => navigate('/ai-agents')}
          className="mt-4 text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          ← Back to Agents
        </button>
      </div>
    );
  }

  // Check if any section has unsaved changes
  const hasAnyChanges = hasBasicInfoChanges() || hasGuardrailsChanges() || hasEscalationChanges() || hasWorkingHoursChanges() || hasVoiceChannelChanges() || hasMeetingVoiceChanges();

  const sectionRefs = {
    basic: 'basic',
    voiceChannel: 'voiceChannel',
    meetingVoice: 'meetingVoice',
    guardrails: 'guardrails',
    escalation: 'escalation',
    workingHours: 'workingHours',
    erpActions: 'erpActions',
    departmentCapabilities: 'departmentCapabilities'
  };

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(`section-${sectionId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (!expandedSections[sectionId]) {
        toggleSection(sectionId);
      }
    }
  };

  const sidebarItems = [
    { id: 'basic', label: 'Basic Info', hasChanges: hasBasicInfoChanges() },
    { id: 'voiceChannel', label: 'Voice Channel', hasChanges: hasVoiceChannelChanges() },
    { id: 'meetingVoice', label: 'Meeting Voice', hasChanges: hasMeetingVoiceChanges() },
    { id: 'guardrails', label: 'Guardrails', hasChanges: hasGuardrailsChanges() },
    { id: 'escalation', label: 'Escalation', hasChanges: hasEscalationChanges() },
    { id: 'workingHours', label: 'Working Hours', hasChanges: hasWorkingHoursChanges() },
    { id: 'erpActions', label: 'ERP Instance', hasChanges: selectedErpInstanceId !== originalErpInstanceId },
    { id: 'departmentCapabilities', label: 'Capabilities', hasChanges: false }
  ];

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto">
      {/* Header - Sticky */}
      <div className="border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-[#111111] z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/ai-agents')}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {agent.agentName} Configuration
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {agent.emailConfig?.address} • {agent.department.toUpperCase()} Department
                </p>
              </div>
            </div>
            {hasAnyChanges && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">Unsaved changes</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Layout: Sidebar + Content */}
      <div className="max-w-6xl mx-auto px-6 py-6 pb-12 flex gap-6">

        {/* Sidebar Navigation */}
        <div className="hidden lg:block w-48 flex-shrink-0">
          <div className="sticky top-24 space-y-1">
            {/* Agent Status Card */}
            <div className="mb-4 p-3 bg-gray-50 dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${agent.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 capitalize">{agent.status}</span>
              </div>
              <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Voice</span>
                  <span className={voiceChannel.enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                    {voiceChannel.enabled ? 'On' : 'Off'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>DIDs</span>
                  <span>{voiceChannel.phoneNumbers.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Dept</span>
                  <span className="capitalize">{agent.department}</span>
                </div>
              </div>
            </div>

            {/* Section Links */}
            {sidebarItems.map(item => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`w-full text-left px-3 py-2 text-xs rounded-lg transition-colors flex items-center justify-between ${
                  expandedSections[item.id]
                    ? 'bg-gray-100 dark:bg-[#222222] text-gray-900 dark:text-white font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                }`}
              >
                <span>{item.label}</span>
                {item.hasChanges && <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" />}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0 space-y-6">

      {/* Basic Info Section */}
      <div id="section-basic" className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 scroll-mt-20">
        <button
          onClick={() => toggleSection('basic')}
          className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#222222]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <ShieldCheckIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-left">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Basic Information</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Agent details and assignment</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasBasicInfoChanges() && (
              <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
            )}
            {expandedSections.basic ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </button>

        {expandedSections.basic && (
          <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-800">
            {/* Profile Photo Row */}
            <div className="mt-4 flex items-center gap-4">
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ring-white dark:ring-gray-800 shadow-sm">
                {profilePhoto ? (
                  <img
                    src={profilePhoto.startsWith('http') ? profilePhoto : `${axiosInstance.defaults.baseURL || ''}${profilePhoto}`}
                    alt={agentName || 'Agent'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-white text-2xl font-semibold">
                    {(agentName || 'A').charAt(0).toUpperCase()}
                  </span>
                )}
                {uploadingPhoto && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Profile Photo</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">PNG, JPG, GIF or WEBP. Max 5MB.</p>
                <div className="flex gap-2">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50 transition-colors"
                  >
                    {profilePhoto ? 'Change' : 'Upload'}
                  </button>
                  {profilePhoto && (
                    <button
                      type="button"
                      onClick={handlePhotoRemove}
                      disabled={uploadingPhoto}
                      className="px-3 py-1.5 border border-red-200 dark:border-red-900/40 text-red-600 dark:text-red-400 rounded-lg text-xs font-medium hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Visualizer identity — animated agent "face" (aura/wave/radial/grid/bar) */}
            <AgentVisualizerSection value={visualizerConfig} onChange={setVisualizerConfig} />

            <div className="mt-5 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Agent Name
                </label>
                <input
                  type="text"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all"
                  placeholder="Enter agent name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={roleKey}
                  onChange={(e) => {
                    const nextKey = e.target.value;
                    setRoleKey(nextKey);
                    // Selecting a role fills the Job Title (still editable after).
                    const row = roleCatalog.find(r => r.roleKey === nextKey);
                    if (row) setJobTitle(row.title);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all cursor-pointer"
                >
                  <option value="">No specific role</option>
                  {roleCatalog.map(r => (
                    <option key={r.roleKey} value={r.roleKey}>
                      {r.title} ({{ junior: 'Junior', senior: 'Senior', lead: 'Lead', manager: 'Manager', director: 'Director', vp: 'VP', c_level: 'C-Level' }[r.level] || r.level})
                    </option>
                  ))}
                </select>
                {roleKey && (
                  <p className="mt-1.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    {roleCatalog.find(r => r.roleKey === roleKey)?.jd}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Job Title
                </label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  maxLength={100}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all"
                  placeholder="e.g. Senior Sales Representative"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Department
                </label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-[#222222] border border-gray-200 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white capitalize">
                  {agent.department}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  maxLength={30}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all"
                  placeholder="e.g. +1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-[#222222] border border-gray-200 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white">
                  {agent.emailConfig?.address}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Assigned User
                </label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-[#222222] border border-gray-200 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white">
                  {agent.userId ? `${agent.userId.firstName} ${agent.userId.lastName}` : 'Not assigned'}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this agent does..."
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{description.length}/500</p>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email Signature
                </label>
                <div className="relative">
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        applySignatureTemplate(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="text-xs border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-[#2a2a2a] text-gray-600 dark:text-gray-300 px-2 py-1 focus:outline-none hover:bg-gray-50 dark:hover:bg-[#222222] cursor-pointer"
                  >
                    <option value="">Load template…</option>
                    <option value="simple">Simple</option>
                    <option value="professional">Professional</option>
                    <option value="minimal">Minimal</option>
                    <option value="contact">With contact icons</option>
                  </select>
                </div>
              </div>

              {/* Signature editor with integrated toolbar */}
              <div className="border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:ring-blue-400 focus-within:border-blue-500 dark:focus-within:border-blue-400 transition-all">
                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#222222]">
                  <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-1 mr-1">
                    Insert
                  </span>
                  {SIGNATURE_VARIABLES.map(v => (
                    <button
                      key={v.token}
                      type="button"
                      onClick={() => insertAtCursor(v.token)}
                      title={v.description}
                      className="px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded border border-blue-200/60 dark:border-blue-800/40 transition-colors"
                    >
                      {v.label}
                    </button>
                  ))}
                  <div className="w-px h-4 bg-gray-300 dark:bg-gray-700 mx-1"></div>
                  <button
                    type="button"
                    onClick={applyDividerToSignature}
                    title="Insert divider line"
                    className="px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition-colors"
                  >
                    ― Divider
                  </button>
                  <button
                    type="button"
                    onClick={() => insertAtCursor('\n')}
                    title="Insert new line"
                    className="px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition-colors"
                  >
                    ↵ Line
                  </button>
                  <div className="flex-1"></div>
                  {signature && (
                    <button
                      type="button"
                      onClick={() => setSignature('')}
                      title="Clear signature"
                      className="px-2 py-0.5 text-[11px] font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Textarea */}
                <textarea
                  ref={signatureRef}
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder={`{full_name}\n{job_title}\n{email}${phoneNumber ? '\n{phone}' : ''}`}
                  rows={5}
                  maxLength={1000}
                  className="w-full px-3 py-2 bg-transparent text-gray-900 dark:text-white text-sm font-mono focus:outline-none resize-y"
                />
              </div>

              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-gray-400">
                  {signature.length}/1000 &middot; Variables render when the email is sent
                </p>
                {signature && /\{\w+\}/.test(signature) && (
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    {(signature.match(/\{\w+\}/g) || []).length} variable{(signature.match(/\{\w+\}/g) || []).length === 1 ? '' : 's'} used
                  </p>
                )}
              </div>

              {/* Live preview */}
              {signature && (
                <div className="mt-3 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
                  <div className="px-3 py-1.5 bg-gray-50 dark:bg-[#222222] border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                    <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Live Preview
                    </span>
                  </div>
                  <pre className="px-3 py-3 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans bg-white dark:bg-[#1a1a1a] leading-relaxed">
{resolveSignaturePreview(signature)}
                  </pre>
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tone
                </label>
                <select
                  value={personality.tone}
                  onChange={(e) => setPersonality(prev => ({ ...prev, tone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                >
                  <option value="formal">Formal</option>
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="casual">Casual</option>
                  <option value="technical">Technical</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Response Style
                </label>
                <select
                  value={personality.responseStyle}
                  onChange={(e) => setPersonality(prev => ({ ...prev, responseStyle: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                >
                  <option value="concise">Concise</option>
                  <option value="detailed">Detailed</option>
                  <option value="technical">Technical</option>
                  <option value="conversational">Conversational</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            {hasBasicInfoChanges() && (
              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-800 mt-4">
                <button
                  onClick={saveBasicInfo}
                  disabled={savingBasicInfo || !agentName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  {savingBasicInfo ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={cancelBasicInfo}
                  disabled={savingBasicInfo}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Voice Channel Configuration Section */}
      <div id="section-voiceChannel" className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 scroll-mt-20">
        <button
          onClick={() => toggleSection('voiceChannel')}
          className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#222222]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <PhoneIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="text-left">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Voice Channel</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Enable phone calls for this agent using ElevenLabs AI voices</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {voiceChannel.enabled && (
              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
                Enabled
              </span>
            )}
            {hasVoiceChannelChanges() && (
              <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
            )}
            {expandedSections.voiceChannel ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </button>

        {expandedSections.voiceChannel && (
          <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-800">
            <div className="mt-4 space-y-6">
              {/* Architecture Info Box */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                      How Voice Channel Works
                    </h4>
                    <p className="text-xs text-blue-800 dark:text-blue-300 mb-2">
                      Your single ElevenLabs conversational AI agent dynamically adapts to each internal agent's personality.
                      When a call arrives, the system automatically uses this agent's voice, personality, and context.
                    </p>
                    <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                      <li>No need to create multiple ElevenLabs agents</li>
                      <li>Each agent has its own voice and personality settings</li>
                      <li>Full context from emails, chats, and previous calls</li>
                      <li>Phone numbers route to specific agents automatically</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Enable/Disable Voice Channel */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Enable Voice Channel</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Allow this agent to make and receive phone calls
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={voiceChannel.enabled}
                    onChange={(e) => setVoiceChannel(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-[#222222] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-700 peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {voiceChannel.enabled && (
                <>
                  {/* Phone Numbers Assignment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number Assignment
                    </label>
                    <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        <strong>Important:</strong> Assign phone numbers (DIDs) to this agent. When calls come to these numbers,
                        this agent's personality and voice will be used. Numbers can only be assigned to one agent at a time.
                      </p>
                    </div>

                    {/* Currently Assigned Summary */}
                    {voiceChannel.phoneNumbers.length > 0 && (
                      <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <p className="text-xs font-medium text-green-700 dark:text-green-300 mb-2">
                          Currently Assigned ({voiceChannel.phoneNumbers.length} number{voiceChannel.phoneNumbers.length !== 1 ? 's' : ''}):
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {didNumbers
                            .filter(num => voiceChannel.phoneNumbers.includes(num._id))
                            .map(num => (
                              <span key={num._id} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs rounded border border-green-200 dark:border-green-700">
                                {num.phoneNumber}
                                {num.friendlyName && <span className="text-green-600 dark:text-green-400">({num.friendlyName})</span>}
                              </span>
                            ))
                          }
                        </div>
                      </div>
                    )}

                    <PhoneNumberMultiSelect
                      selectedNumbers={voiceChannel.phoneNumbers}
                      onChange={(numbers) => setVoiceChannel(prev => ({ ...prev, phoneNumbers: numbers }))}
                      availableNumbers={didNumbers}
                      loading={loadingDIDs}
                      disabled={false}
                      currentAgentId={agentId}
                    />
                  </div>

                  {/* Voice Selection - COMMENTED OUT: Voice is configured in ElevenLabs dashboard */}
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      ElevenLabs Voice Selection
                    </label>
                    <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <strong>How it works:</strong> Select which ElevenLabs voice this agent will use for calls.
                        Your single ElevenLabs agent dynamically uses different voices based on which internal agent is handling the call.
                      </p>
                    </div>
                    <VoiceSelector
                      value={voiceChannel.voiceId}
                      onChange={(voiceId) => setVoiceChannel(prev => ({ ...prev, voiceId }))}
                      disabled={false}
                    />
                  </div> */}

                  {/* Voice Settings */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Voice Settings
                    </label>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">Stability</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {(voiceChannel.voiceSettings.stability * 100).toFixed(0)}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={voiceChannel.voiceSettings.stability}
                          onChange={(e) => setVoiceChannel(prev => ({
                            ...prev,
                            voiceSettings: { ...prev.voiceSettings, stability: parseFloat(e.target.value) }
                          }))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-[#222222]"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Higher stability = more consistent voice
                        </p>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">Similarity</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {(voiceChannel.voiceSettings.similarity * 100).toFixed(0)}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={voiceChannel.voiceSettings.similarity}
                          onChange={(e) => setVoiceChannel(prev => ({
                            ...prev,
                            voiceSettings: { ...prev.voiceSettings, similarity: parseFloat(e.target.value) }
                          }))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-[#222222]"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Higher similarity = closer to original voice
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Call Behavior */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Call Behavior
                    </label>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            Max Duration (minutes)
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="60"
                            value={voiceChannel.callBehavior.maxDuration}
                            onChange={(e) => setVoiceChannel(prev => ({
                              ...prev,
                              callBehavior: { ...prev.callBehavior, maxDuration: parseInt(e.target.value) || 15 }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            On Max Duration
                          </label>
                          <select
                            value={voiceChannel.callBehavior.onMaxDuration}
                            onChange={(e) => setVoiceChannel(prev => ({
                              ...prev,
                              callBehavior: { ...prev.callBehavior, onMaxDuration: e.target.value }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                          >
                            <option value="transfer_to_human">Transfer to Human</option>
                            <option value="end_call">End Call</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                          Voicemail Behavior
                        </label>
                        <select
                          value={voiceChannel.callBehavior.voicemailBehavior}
                          onChange={(e) => setVoiceChannel(prev => ({
                            ...prev,
                            callBehavior: { ...prev.callBehavior, voicemailBehavior: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                        >
                          <option value="leave_message">Leave Message</option>
                          <option value="hangup">Hang Up</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={voiceChannel.callBehavior.recordCalls}
                            onChange={(e) => setVoiceChannel(prev => ({
                              ...prev,
                              callBehavior: { ...prev.callBehavior, recordCalls: e.target.checked }
                            }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Record all calls</span>
                        </label>

                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={voiceChannel.callBehavior.transcribeCalls}
                            onChange={(e) => setVoiceChannel(prev => ({
                              ...prev,
                              callBehavior: { ...prev.callBehavior, transcribeCalls: e.target.checked }
                            }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Transcribe all calls</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Action Buttons */}
              {hasVoiceChannelChanges() && (
                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                  <button
                    onClick={saveVoiceChannel}
                    disabled={savingVoiceChannel}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    {savingVoiceChannel ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={cancelVoiceChannel}
                    disabled={savingVoiceChannel}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Meeting Voice Section */}
      <div id="section-meetingVoice" className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 scroll-mt-20">
        <button
          onClick={() => toggleSection('meetingVoice')}
          className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#222222]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
              <SpeakerWaveIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
            </div>
            <div className="text-left">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Meeting Voice</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Configure the Cartesia voice this agent uses in meetings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {meetingVoice.voiceId && (
              <span className="px-2 py-1 text-xs font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 rounded">
                Configured
              </span>
            )}
            {hasMeetingVoiceChanges() && (
              <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
            )}
            {expandedSections.meetingVoice ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </button>

        {expandedSections.meetingVoice && (
          <div className="border-t border-gray-200 dark:border-gray-800">
            <div className="px-6 py-5 space-y-6">

              {/* Gender Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Voice Gender</label>
                <div className="flex gap-3">
                  {[
                    { value: 'female', label: 'Female' },
                    { value: 'male', label: 'Male' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setMeetingVoice(prev => ({ ...prev, gender: option.value, voiceId: '' }));
                        fetchCartesiaVoices(option.value);
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        meetingVoice.gender === option.value
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setMeetingVoice(prev => ({ ...prev, gender: '' }));
                      fetchCartesiaVoices();
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      meetingVoice.gender === ''
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    All
                  </button>
                  {meetingVoice.gender && (
                    <button
                      onClick={() => {
                        setMeetingVoice(prev => ({ ...prev, gender: '', voiceId: '' }));
                        setCartesiaVoices([]);
                      }}
                      className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Voice Selection Grid */}
              {(meetingVoice.gender || cartesiaVoices.length > 0) && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Select Voice {cartesiaVoices.length > 0 && <span className="text-gray-400 font-normal">({cartesiaVoices.length} voices)</span>}
                    </label>
                    <button
                      onClick={syncCartesiaVoices}
                      disabled={syncingVoices}
                      className="text-xs text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 disabled:opacity-50"
                    >
                      {syncingVoices ? 'Syncing...' : 'Refresh Voices'}
                    </button>
                  </div>
                  {loadingCartesiaVoices ? (
                    <div className="flex items-center justify-center py-8 text-gray-400">
                      <svg className="animate-spin w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Loading voices...
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-1">
                      {cartesiaVoices.map(voice => (
                        <div
                          key={voice.id}
                          className={`p-3 rounded-lg border text-left transition-colors cursor-pointer ${
                            meetingVoice.voiceId === voice.id
                              ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                          onClick={() => setMeetingVoice(prev => ({ ...prev, voiceId: voice.id }))}
                        >
                          <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{voice.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                {voice.description || voice.language || voice.gender}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                previewVoice(voice.id, voice.name);
                              }}
                              className={`ml-2 flex-shrink-0 p-1.5 rounded-full transition-colors ${
                                previewingVoiceId === voice.id
                                  ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-600'
                                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                              }`}
                              title={previewingVoiceId === voice.id ? 'Stop preview' : 'Preview voice'}
                            >
                              {previewingVoiceId === voice.id ? (
                                <StopIcon className="w-4 h-4" />
                              ) : (
                                <PlayIcon className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Speaking Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Speaking Rate: {meetingVoice.speakingRate}x
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={meetingVoice.speakingRate}
                  onChange={(e) => setMeetingVoice(prev => ({ ...prev, speakingRate: parseFloat(e.target.value) }))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0.5x (Slow)</span>
                  <span>1.0x (Normal)</span>
                  <span>2.0x (Fast)</span>
                </div>
              </div>

              {/* Action Buttons */}
              {hasMeetingVoiceChanges() && (
                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                  <button
                    onClick={saveMeetingVoice}
                    disabled={savingMeetingVoice}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    {savingMeetingVoice ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={cancelMeetingVoice}
                    disabled={savingMeetingVoice}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Behavioral Guardrails Section */}
      <div id="section-guardrails" className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 scroll-mt-20">
        <button
          onClick={() => toggleSection('guardrails')}
          className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#222222]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <ShieldCheckIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="text-left">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Behavioral Guardrails</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Define what this agent can and cannot do</p>
            </div>
          </div>
          {hasGuardrailsChanges() && (
            <div className="mr-2 w-2 h-2 rounded-full bg-yellow-500"></div>
          )}
          {expandedSections.guardrails ? (
            <ChevronUpIcon className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
          )}
        </button>

        {expandedSections.guardrails && (
          <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-800">
            <div className="mt-4 space-y-6">
              {/* Auto Approve Actions */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Auto-Approve Actions
                  </label>
                  <span className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded-full">
                    {guardrails.autoApprove?.length || 0} active
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 ml-7">
                  Agent can perform these actions without requiring approval
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 mt-1 uppercase tracking-wider font-medium">General</p>
                <div className="grid grid-cols-2 gap-2">
                  {genericAutoApprove.map(option => {
                    const isChecked = guardrails.autoApprove?.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleGuardrailAction('autoApprove', option.value)}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all text-left ${
                          isChecked
                            ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/15'
                            : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222]/50'
                        }`}
                      >
                        <div className={`w-4.5 h-4.5 flex-shrink-0 rounded flex items-center justify-center transition-colors ${
                          isChecked
                            ? 'bg-green-500 dark:bg-green-600'
                            : 'border-2 border-gray-300 dark:border-gray-600'
                        }`} style={{ width: '18px', height: '18px' }}>
                          {isChecked && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                          )}
                        </div>
                        <span className={`text-sm ${isChecked ? 'text-green-800 dark:text-green-200 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {(deptAutoApproveMap[agent?.department] || []).length > 0 && (
                  <>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 mt-4 uppercase tracking-wider font-medium">
                      {agent.department.charAt(0).toUpperCase() + agent.department.slice(1)} Department
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {(deptAutoApproveMap[agent?.department] || []).map(option => {
                        const isChecked = guardrails.autoApprove?.includes(option.value);
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => toggleGuardrailAction('autoApprove', option.value)}
                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all text-left ${
                              isChecked
                                ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/15'
                                : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222]/50'
                            }`}
                          >
                            <div className={`flex-shrink-0 rounded flex items-center justify-center transition-colors ${
                              isChecked
                                ? 'bg-green-500 dark:bg-green-600'
                                : 'border-2 border-gray-300 dark:border-gray-600'
                            }`} style={{ width: '18px', height: '18px' }}>
                              {isChecked && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                              )}
                            </div>
                            <span className={`text-sm ${isChecked ? 'text-green-800 dark:text-green-200 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                              {option.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Require Approval Actions */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <svg className="w-3 h-3 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Require Approval
                  </label>
                  <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full">
                    {guardrails.requireApproval?.length || 0} active
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 ml-7">
                  Agent must request approval before performing these actions
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wider font-medium">General</p>
                <div className="grid grid-cols-2 gap-2">
                  {genericRequireApproval.map(option => {
                    const isChecked = guardrails.requireApproval?.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleGuardrailAction('requireApproval', option.value)}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all text-left ${
                          isChecked
                            ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/15'
                            : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222]/50'
                        }`}
                      >
                        <div className={`flex-shrink-0 rounded flex items-center justify-center transition-colors ${
                          isChecked
                            ? 'bg-amber-500 dark:bg-amber-600'
                            : 'border-2 border-gray-300 dark:border-gray-600'
                        }`} style={{ width: '18px', height: '18px' }}>
                          {isChecked && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                          )}
                        </div>
                        <span className={`text-sm ${isChecked ? 'text-amber-800 dark:text-amber-200 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {(deptRequireApprovalMap[agent?.department] || []).length > 0 && (
                  <>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 mt-4 uppercase tracking-wider font-medium">
                      {agent.department.charAt(0).toUpperCase() + agent.department.slice(1)} Department
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {(deptRequireApprovalMap[agent?.department] || []).map(option => {
                        const isChecked = guardrails.requireApproval?.includes(option.value);
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => toggleGuardrailAction('requireApproval', option.value)}
                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all text-left ${
                              isChecked
                                ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/15'
                                : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222]/50'
                            }`}
                          >
                            <div className={`flex-shrink-0 rounded flex items-center justify-center transition-colors ${
                              isChecked
                                ? 'bg-amber-500 dark:bg-amber-600'
                                : 'border-2 border-gray-300 dark:border-gray-600'
                            }`} style={{ width: '18px', height: '18px' }}>
                              {isChecked && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                              )}
                            </div>
                            <span className={`text-sm ${isChecked ? 'text-amber-800 dark:text-amber-200 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                              {option.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Spending Limit - Only show for non-HR departments */}
              {agent?.department !== 'hr' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Spending Limit (USD)
                  </label>
                  <input
                    type="number"
                    value={guardrails.spendingLimit}
                    onChange={(e) => setGuardrails(prev => ({ ...prev, spendingLimit: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    className="w-48 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Maximum amount agent can approve without escalation
                  </p>
                </div>
              )}

              {/* Data Access Controls */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <svg className="w-3 h-3 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  </div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Data Access Controls
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 ml-7">
                  Control what data this agent can access
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'canAccessPII', label: 'Access PII Data', desc: 'Personally identifiable information' },
                    { key: 'canAccessFinancialData', label: 'Access Financial Data', desc: 'Invoices, payments, budgets' },
                    { key: 'canAccessConfidentialData', label: 'Access Confidential Data', desc: 'Restricted/classified data' },
                    { key: 'canAccessAllDepartments', label: 'Cross-Department Access', desc: 'Access data from other departments' },
                    { key: 'auditAllActions', label: 'Audit All Actions', desc: 'Log every action for compliance' }
                  ].map(item => {
                    const isChecked = guardrails.dataAccessControls?.[item.key] || false;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setGuardrails(prev => ({
                          ...prev,
                          dataAccessControls: {
                            ...prev.dataAccessControls,
                            [item.key]: !prev.dataAccessControls?.[item.key]
                          }
                        }))}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all text-left ${
                          isChecked
                            ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/15'
                            : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222]/50'
                        }`}
                      >
                        <div className={`flex-shrink-0 rounded flex items-center justify-center transition-colors ${
                          isChecked
                            ? 'bg-blue-500 dark:bg-blue-600'
                            : 'border-2 border-gray-300 dark:border-gray-600'
                        }`} style={{ width: '18px', height: '18px' }}>
                          {isChecked && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                          )}
                        </div>
                        <div>
                          <span className={`text-sm ${isChecked ? 'text-blue-800 dark:text-blue-200 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                            {item.label}
                          </span>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{item.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Test Guardrails */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Test Guardrails
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Dry-run an action against this agent's guardrails to see if it would be approved or denied
                </p>
                <div className="flex gap-2 items-end flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Action Code</label>
                    <select
                      value={guardrailTestAction}
                      onChange={(e) => { setGuardrailTestAction(e.target.value); setGuardrailTestResult(null); }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                    >
                      <option value="">Select action...</option>
                      {allActionCodes.map(code => (
                        <option key={code} value={code}>{code.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Amount (optional)</label>
                    <input
                      type="number"
                      value={guardrailTestAmount}
                      onChange={(e) => setGuardrailTestAmount(e.target.value)}
                      placeholder="0"
                      min="0"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                    />
                  </div>
                  <button
                    onClick={testGuardrail}
                    disabled={!guardrailTestAction || testingGuardrail}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors text-sm font-medium whitespace-nowrap"
                  >
                    {testingGuardrail ? 'Testing...' : 'Run Test'}
                  </button>
                </div>
                {guardrailTestResult && (
                  <div className={`mt-3 p-3 rounded-lg border text-sm ${
                    guardrailTestResult.approved
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
                  }`}>
                    <div className="flex items-center gap-2 font-medium">
                      <span>{guardrailTestResult.approved ? '✓' : '✗'}</span>
                      <span>{guardrailTestResult.approved ? 'APPROVED' : 'DENIED'}</span>
                      <span className="text-xs font-normal opacity-75">({guardrailTestResult.reason?.replace(/_/g, ' ')})</span>
                    </div>
                    {guardrailTestResult.warnings?.length > 0 && (
                      <ul className="mt-1 text-xs opacity-75 list-disc list-inside">
                        {guardrailTestResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {hasGuardrailsChanges() && (
                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                  <button
                    onClick={saveGuardrails}
                    disabled={savingGuardrails}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    {savingGuardrails ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={cancelGuardrails}
                    disabled={savingGuardrails}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Escalation & Reporting Section */}
      <div id="section-escalation" className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 scroll-mt-20">
        <button
          onClick={() => toggleSection('escalation')}
          className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#222222]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <BellAlertIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="text-left">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Escalation & Reporting</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Configure who this agent reports to</p>
            </div>
          </div>
          {hasEscalationChanges() && (
            <div className="mr-2 w-2 h-2 rounded-full bg-yellow-500"></div>
          )}
          {expandedSections.escalation ? (
            <ChevronUpIcon className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
          )}
        </button>

        {expandedSections.escalation && (
          <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-800">
            <div className="mt-4 space-y-6">
              {/* Report To User */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Report To
                </label>
                <SearchableSelect
                  value={escalation.reportToUserId}
                  onChange={(value) => setEscalation(prev => ({ ...prev, reportToUserId: value }))}
                  options={userOptions}
                  placeholder="Select manager/supervisor..."
                  disabled={loadingUsers}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  User who receives escalations from this agent
                </p>
              </div>

              {/* Sentiment Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sentiment Threshold: {escalation.sentimentThreshold}
                </label>
                <input
                  type="range"
                  min="-1"
                  max="1"
                  step="0.1"
                  value={escalation.sentimentThreshold}
                  onChange={(e) => setEscalation(prev => ({ ...prev, sentimentThreshold: parseFloat(e.target.value) }))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Escalate if sentiment is more negative than this threshold
                </p>
              </div>

              {/* Complexity Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Complexity Threshold: {escalation.complexityThreshold}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={escalation.complexityThreshold}
                  onChange={(e) => setEscalation(prev => ({ ...prev, complexityThreshold: parseInt(e.target.value) }))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Escalate if complexity exceeds this threshold
                </p>
              </div>

              {/* Minimum Confidence */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Minimum Confidence: {escalation.minConfidence}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={escalation.minConfidence}
                  onChange={(e) => setEscalation(prev => ({ ...prev, minConfidence: parseInt(e.target.value) }))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Escalate if AI confidence is below this threshold
                </p>
              </div>

              {/* Escalate After Thresholds */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Auto-Escalate After
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Failed Attempts
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={escalation.escalateAfter?.attempts ?? 3}
                      onChange={(e) => setEscalation(prev => ({
                        ...prev,
                        escalateAfter: { ...prev.escalateAfter, attempts: parseInt(e.target.value) || 3 }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Escalate after this many failed attempts
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Elapsed Minutes
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1440"
                      value={escalation.escalateAfter?.minutes ?? 60}
                      onChange={(e) => setEscalation(prev => ({
                        ...prev,
                        escalateAfter: { ...prev.escalateAfter, minutes: parseInt(e.target.value) || 60 }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Escalate if unresolved after this many minutes
                    </p>
                  </div>
                </div>
              </div>

              {/* Urgency Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Urgency Keywords
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Escalate when these keywords appear in the interaction content
                </p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addUrgencyKeyword())}
                    placeholder="Add keyword..."
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={addUrgencyKeyword}
                    disabled={!newKeyword.trim()}
                    className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm"
                  >
                    Add
                  </button>
                </div>
                {escalation.urgencyKeywords?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {escalation.urgencyKeywords.map((kw) => (
                      <span
                        key={kw}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 rounded-md text-xs"
                      >
                        {kw}
                        <button
                          onClick={() => removeUrgencyKeyword(kw)}
                          className="ml-1 text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Escalation Events */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <svg className="w-3 h-3 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Escalation Triggers
                  </label>
                  <span className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded-full">
                    {escalation.escalateOn?.length || 0} active
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 ml-7">
                  Escalate when any of these conditions are detected
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {escalationEventOptions.map(option => {
                    const isChecked = escalation.escalateOn?.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleEscalateOn(option.value)}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all text-left ${
                          isChecked
                            ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/15'
                            : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222]/50'
                        }`}
                      >
                        <div className={`flex-shrink-0 rounded flex items-center justify-center transition-colors ${
                          isChecked
                            ? 'bg-red-500 dark:bg-red-600'
                            : 'border-2 border-gray-300 dark:border-gray-600'
                        }`} style={{ width: '18px', height: '18px' }}>
                          {isChecked && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                          )}
                        </div>
                        <span className={`text-sm ${isChecked ? 'text-red-800 dark:text-red-200 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Test Escalation */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Test Escalation
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Simulate an interaction to see if it would trigger escalation
                </p>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Sentiment ({escalationTestParams.sentiment})
                    </label>
                    <input
                      type="range"
                      min="-1"
                      max="1"
                      step="0.1"
                      value={escalationTestParams.sentiment}
                      onChange={(e) => setEscalationTestParams(prev => ({ ...prev, sentiment: parseFloat(e.target.value) }))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Complexity ({escalationTestParams.complexity}%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={escalationTestParams.complexity}
                      onChange={(e) => setEscalationTestParams(prev => ({ ...prev, complexity: parseInt(e.target.value) }))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Confidence ({escalationTestParams.confidence}%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={escalationTestParams.confidence}
                      onChange={(e) => setEscalationTestParams(prev => ({ ...prev, confidence: parseInt(e.target.value) }))}
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Sample Content (optional)</label>
                    <input
                      type="text"
                      value={escalationTestParams.content}
                      onChange={(e) => setEscalationTestParams(prev => ({ ...prev, content: e.target.value }))}
                      placeholder="e.g. I need to speak to a manager urgently"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                    />
                  </div>
                  <button
                    onClick={testEscalation}
                    disabled={testingEscalation}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors text-sm font-medium whitespace-nowrap"
                  >
                    {testingEscalation ? 'Testing...' : 'Run Test'}
                  </button>
                </div>
                {escalationTestResult && (
                  <div className={`mt-3 p-3 rounded-lg border text-sm ${
                    escalationTestResult.shouldEscalate
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
                      : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
                  }`}>
                    <div className="flex items-center gap-2 font-medium">
                      <span>{escalationTestResult.shouldEscalate ? '🚨' : '✓'}</span>
                      <span>{escalationTestResult.shouldEscalate ? 'WOULD ESCALATE' : 'NO ESCALATION'}</span>
                      <span className="text-xs font-normal opacity-75">(score: {escalationTestResult.score})</span>
                    </div>
                    {escalationTestResult.reasons?.length > 0 && (
                      <ul className="mt-1 text-xs opacity-75 list-disc list-inside">
                        {escalationTestResult.reasons.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    )}
                    {escalationTestResult.escalateTo && (
                      <p className="mt-1 text-xs opacity-75">
                        Escalate to: {escalationTestResult.escalateTo.name || escalationTestResult.escalateTo.email || 'Not configured'}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {hasEscalationChanges() && (
                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                  <button
                    onClick={saveEscalation}
                    disabled={savingEscalation}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    {savingEscalation ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={cancelEscalation}
                    disabled={savingEscalation}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Working Hours & SLA Section */}
      <div id="section-workingHours" className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 scroll-mt-20">
        <button
          onClick={() => toggleSection('workingHours')}
          className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#222222]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <ClockIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-left">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Working Hours & SLA</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Configure response time targets</p>
            </div>
          </div>
          {hasWorkingHoursChanges() && (
            <div className="mr-2 w-2 h-2 rounded-full bg-yellow-500"></div>
          )}
          {expandedSections.workingHours ? (
            <ChevronUpIcon className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
          )}
        </button>

        {expandedSections.workingHours && (
          <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-800">
            <div className="mt-4 space-y-6">
              {/* Enable Working Hours */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Enable Working Hours</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    When disabled, agent operates 24/7
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={workingHours.enabled}
                    onChange={(e) => setWorkingHours(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-[#222222] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-700 peer-checked:bg-green-600"></div>
                </label>
              </div>

              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Timezone
                </label>
                <select
                  value={workingHours.timezone}
                  onChange={(e) => setWorkingHours(prev => ({ ...prev, timezone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                >
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="America/Anchorage">Alaska Time (AKT)</option>
                  <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                  <option value="Europe/London">GMT/BST (London)</option>
                  <option value="Europe/Paris">CET (Paris)</option>
                  <option value="Asia/Dubai">GST (Dubai)</option>
                  <option value="Asia/Kolkata">IST (India)</option>
                  <option value="Asia/Shanghai">CST (China)</option>
                  <option value="Asia/Tokyo">JST (Japan)</option>
                  <option value="Australia/Sydney">AEST (Sydney)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>

              {/* Weekly Schedule */}
              {workingHours.enabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Weekly Schedule
                  </label>
                  <div className="space-y-2">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                      const daySchedule = workingHours.schedule?.find(s => s.day === day);
                      const isEnabled = !!daySchedule;
                      return (
                        <div key={day} className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setWorkingHours(prev => {
                                const schedule = [...(prev.schedule || [])];
                                const idx = schedule.findIndex(s => s.day === day);
                                if (idx >= 0) {
                                  schedule.splice(idx, 1);
                                } else {
                                  schedule.push({ day, startTime: '09:00', endTime: '17:00' });
                                }
                                return { ...prev, schedule };
                              });
                            }}
                            className={`w-24 text-left text-sm py-1.5 px-2 rounded transition-colors ${
                              isEnabled
                                ? 'text-gray-900 dark:text-white font-medium'
                                : 'text-gray-400 dark:text-gray-600 line-through'
                            }`}
                          >
                            {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                          </button>
                          {isEnabled ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="time"
                                value={daySchedule?.startTime || '09:00'}
                                onChange={(e) => {
                                  setWorkingHours(prev => {
                                    const schedule = prev.schedule.map(s =>
                                      s.day === day ? { ...s, startTime: e.target.value } : s
                                    );
                                    return { ...prev, schedule };
                                  });
                                }}
                                className="px-2 py-1.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                              />
                              <span className="text-gray-400 text-xs">to</span>
                              <input
                                type="time"
                                value={daySchedule?.endTime || '17:00'}
                                onChange={(e) => {
                                  setWorkingHours(prev => {
                                    const schedule = prev.schedule.map(s =>
                                      s.day === day ? { ...s, endTime: e.target.value } : s
                                    );
                                    return { ...prev, schedule };
                                  });
                                }}
                                className="px-2 py-1.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                              />
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-600">Off — click day to enable</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Response Targets */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Response Time Targets (minutes)
                </label>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Urgent Priority</span>
                    <input
                      type="number"
                      value={workingHours.responseTargets.urgent}
                      onChange={(e) => setWorkingHours(prev => ({
                        ...prev,
                        responseTargets: { ...prev.responseTargets, urgent: parseInt(e.target.value) || 1 }
                      }))}
                      min="0"
                      className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Standard Priority</span>
                    <input
                      type="number"
                      value={workingHours.responseTargets.standard}
                      onChange={(e) => setWorkingHours(prev => ({
                        ...prev,
                        responseTargets: { ...prev.responseTargets, standard: parseInt(e.target.value) || 5 }
                      }))}
                      min="0"
                      className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4 items-center">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Low Priority</span>
                    <input
                      type="number"
                      value={workingHours.responseTargets.low}
                      onChange={(e) => setWorkingHours(prev => ({
                        ...prev,
                        responseTargets: { ...prev.responseTargets, low: parseInt(e.target.value) || 15 }
                      }))}
                      min="0"
                      className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* After Hours Behavior */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  After Hours Behavior
                </label>
                <select
                  value={workingHours.afterHoursBehavior}
                  onChange={(e) => setWorkingHours(prev => ({ ...prev, afterHoursBehavior: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white"
                >
                  <option value="full_service">Full Service (24/7)</option>
                  <option value="auto_respond">Auto-respond Only</option>
                  <option value="emergency_only">Emergency Only</option>
                  <option value="off">Off</option>
                </select>
              </div>

              {/* Out of Office Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Out of Office Message
                </label>
                <textarea
                  value={workingHours.outOfOfficeMessage}
                  onChange={(e) => setWorkingHours(prev => ({ ...prev, outOfOfficeMessage: e.target.value }))}
                  placeholder="e.g. Thank you for reaching out. Our team is currently out of office and will respond during business hours."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm resize-none"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Auto-reply sent outside working hours (when after-hours behavior is auto-respond)
                </p>
              </div>

              {/* Action Buttons */}
              {hasWorkingHoursChanges() && (
                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                  <button
                    onClick={saveWorkingHours}
                    disabled={savingWorkingHours}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    {savingWorkingHours ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={cancelWorkingHours}
                    disabled={savingWorkingHours}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ERP Instance Section */}
      <div id="section-erpActions" className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 scroll-mt-20">
        <button
          onClick={() => { toggleSection('erpActions'); if (!expandedSections.erpActions) fetchErpInstances(); }}
          className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#222222]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="text-left">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">ERP Instance</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Tellency ERP instance this agent can access</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedErpInstanceId && (
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                Connected
              </span>
            )}
            {expandedSections.erpActions ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </button>

        {expandedSections.erpActions && (
          <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-800">
            <div className="mt-4 space-y-4">
              {loadingErpData ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                </div>
              ) : erpInstances.length === 0 ? (
                <div className="p-6 text-center border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                  <svg className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">No ERP Instances</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Create a Tellency ERP instance first to connect this agent.</p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Select which Tellency ERP instance this agent will operate on. Actions and approvals are managed within the ERP for this agent's account.
                  </p>

                  <div className="grid grid-cols-1 gap-3">
                    {/* None option */}
                    <label
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                        !selectedErpInstanceId
                          ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10 ring-1 ring-indigo-500'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="erpInstance"
                        checked={!selectedErpInstanceId}
                        onChange={() => setSelectedErpInstanceId(null)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">No ERP Access</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Agent will not have access to any ERP instance</p>
                      </div>
                    </label>

                    {/* Instance options */}
                    {erpInstances.map((instance) => {
                      const instanceId = instance._id;
                      const isSelected = selectedErpInstanceId === instanceId;
                      const statusColors = {
                        running: 'bg-green-500',
                        stopped: 'bg-gray-400',
                        maintenance: 'bg-yellow-500',
                        provisioning: 'bg-blue-500',
                        failed: 'bg-red-500',
                        error: 'bg-red-500'
                      };
                      const envLabels = {
                        production: { text: 'Production', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
                        staging: { text: 'Staging', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
                        development: { text: 'Dev', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' }
                      };
                      const env = envLabels[instance.environment] || envLabels.production;

                      return (
                        <label
                          key={instanceId}
                          className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                            isSelected
                              ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10 ring-1 ring-indigo-500'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          } ${instance.status !== 'running' ? 'opacity-60' : ''}`}
                        >
                          <input
                            type="radio"
                            name="erpInstance"
                            checked={isSelected}
                            onChange={() => setSelectedErpInstanceId(instanceId)}
                            className="w-4 h-4 mt-0.5 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {instance.displayName || instance.subdomain}
                              </span>
                              <span className={`w-2 h-2 rounded-full ${statusColors[instance.status] || 'bg-gray-400'}`} title={instance.status} />
                              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${env.color}`}>
                                {env.text}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                              {instance.fullUrl || `${instance.subdomain}.tellency.com`}
                            </p>
                            {instance.modules?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {instance.modules.slice(0, 5).map((mod) => (
                                  <span key={mod} className="px-1.5 py-0.5 text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">
                                    {mod}
                                  </span>
                                ))}
                                {instance.modules.length > 5 && (
                                  <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded">
                                    +{instance.modules.length - 5} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* Save button - only show when changed */}
                  {selectedErpInstanceId !== originalErpInstanceId && (
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-800">
                      <span className="text-xs text-amber-600 dark:text-amber-400">Unsaved changes</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedErpInstanceId(originalErpInstanceId)}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveErpInstance}
                          disabled={savingErpInstance}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 transition-colors flex items-center gap-1.5"
                        >
                          {savingErpInstance && (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          )}
                          Save
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Department-Specific Capabilities Section */}
      {agent && agent.department && agent.department !== 'custom' && (
        <div id="section-departmentCapabilities" className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 scroll-mt-20">
          <button
            onClick={() => toggleSection('departmentCapabilities')}
            className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#222222]/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="text-left">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  {agent.department.charAt(0).toUpperCase() + agent.department.slice(1)} Capabilities
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Configure department-specific features and workflows
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {expandedSections.departmentCapabilities ? (
                <ChevronUpIcon className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </button>

          {expandedSections.departmentCapabilities && (
            <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-800">
              <div className="mt-4">
                {agent.department === 'finance' && (
                  <FinanceCapabilities
                    agentId={agentId}
                    showNotification={showNotification}
                  />
                )}
                {agent.department === 'hr' && (
                  <HRCapabilities
                    agentId={agentId}
                    showNotification={showNotification}
                  />
                )}
                {agent.department === 'it' && (
                  <ITCapabilities
                    agentId={agentId}
                    showNotification={showNotification}
                  />
                )}
                {agent.department === 'sales' && (
                  <SalesCapabilities
                    agentId={agentId}
                    showNotification={showNotification}
                  />
                )}
                {agent.department === 'legal' && (
                  <LegalCapabilities
                    agentId={agentId}
                    showNotification={showNotification}
                  />
                )}
                {agent.department === 'support' && (
                  <SupportCapabilities
                    agentId={agentId}
                    showNotification={showNotification}
                  />
                )}
                {agent.department === 'marketing' && (
                  <MarketingCapabilities
                    agentId={agentId}
                    showNotification={showNotification}
                  />
                )}
                {!['finance', 'hr', 'it', 'sales', 'legal', 'support', 'marketing'].includes(agent.department) && (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    <p>No specific capabilities available for this department type.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      </div>
      {/* End Main Content */}
      </div>
      {/* End Layout */}
    </div>
  );
};

export default AIAgentConfig;
